import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiCreditCard,
  FiSave,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeeSelector
  from "../common/EmployeeSelector";

import {
  ADVANCE_RECOVERY_TYPES,
} from "../utils/employee.constants";

import {
  formatEmployeeCurrency,
  formatEmployeeDateInput,
  formatEmployeeLabel,
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeAdvance.css";


const INITIAL_FORM = {
  employeeId: "",
  advanceDate: "",
  advanceAmount: "",
  recoveryType: "MANUAL",
  recoveryAmountPerMonth: "",
  reason: "",
  remark: "",
};


const AddEditAdvanceModal = ({
  open,

  advance,

  employees = [],

  onClose,

  onSuccess,
}) => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    form,
    setForm,
  ] =
    useState({
      ...INITIAL_FORM,
    });


  const [
    errors,
    setErrors,
  ] =
    useState({});


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const isEdit =
    Boolean(
      advance?._id
    );


  useEffect(
    () => {

      if (
        !open
      ) {

        return;

      }


      if (
        advance
      ) {

        setForm({
          employeeId:
            advance?.employeeId?._id ||
            advance?.employeeId ||
            "",

          advanceDate:
            formatEmployeeDateInput(
              advance?.advanceDate ||
              advance?.date
            ),

          advanceAmount:
            String(
              advance?.advanceAmount ??
              advance?.amount ??
              ""
            ),

          recoveryType:
            advance?.recoveryType ||
            "MANUAL",

          recoveryAmountPerMonth:
            String(
              advance?.recoveryAmountPerMonth ??
              advance?.monthlyRecoveryAmount ??
              ""
            ),

          reason:
            advance?.reason ||
            advance?.purpose ||
            "",

          remark:
            advance?.remark ||
            advance?.notes ||
            "",
        });

      } else {

        setForm({
          ...INITIAL_FORM,

          advanceDate:
            new Date()
              .toISOString()
              .slice(
                0,
                10
              ),
        });

      }


      setErrors(
        {}
      );

    },
    [
      open,
      advance,
    ]
  );


  if (
    !open
  ) {

    return null;

  }


  const handleChange = (
    event
  ) => {

    const {
      name,
      value,
    } =
      event.target;


    let nextValue =
      value;


    if (
      [
        "advanceAmount",
        "recoveryAmountPerMonth",
      ].includes(
        name
      )
    ) {

      nextValue =
        value.replace(
          /[^\d.]/g,
          ""
        );

    }


    setForm(
      (
        previous
      ) => ({
        ...previous,

        [name]:
          nextValue,
      })
    );


    setErrors(
      (
        previous
      ) => ({
        ...previous,

        [name]:
          "",

        form:
          "",
      })
    );

  };


  const validate =
    () => {

      const nextErrors =
        {};


      if (
        !form.employeeId
      ) {

        nextErrors.employeeId =
          "Employee is required.";

      }


      if (
        !form.advanceDate
      ) {

        nextErrors.advanceDate =
          "Advance date is required.";

      }


      if (
        Number(
          form.advanceAmount
        ) <=
        0
      ) {

        nextErrors.advanceAmount =
          "Enter a valid advance amount.";

      }


      if (
        !form.reason.trim()
      ) {

        nextErrors.reason =
          "Advance reason is required.";

      }


      if (
        [
          "SALARY",
          "BOTH",
        ].includes(
          form.recoveryType
        ) &&
        Number(
          form.recoveryAmountPerMonth
        ) <=
        0
      ) {

        nextErrors.recoveryAmountPerMonth =
          "Monthly recovery amount is required.";

      }


      setErrors(
        nextErrors
      );


      return (
        Object.keys(
          nextErrors
        ).length ===
        0
      );

    };


  const handleSubmit =
    async (
      event
    ) => {

      event.preventDefault();


      if (
        !validate()
      ) {

        return;

      }


      try {

        setLoading(
          true
        );


        const payload = {
          employeeId:
            form.employeeId,

          advanceDate:
            form.advanceDate,

          advanceAmount:
            Number(
              form.advanceAmount
            ),

          recoveryType:
            form.recoveryType,

          recoveryAmountPerMonth:
            Number(
              form.recoveryAmountPerMonth ||
              0
            ),

          reason:
            form.reason.trim(),

          remark:
            form.remark.trim(),
        };


        const response =
          isEdit
            ?
            await api.put(
              config.updateEmployeeAdvance(
                advance._id
              ),
              payload
            )
            :
            await api.post(
              config.createEmployeeAdvance,
              payload
            );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to save employee advance."
          );

        }


        await onSuccess?.(
          response?.data
        );

      } catch (
        requestError
      ) {

        setErrors({
          form:
            getEmployeeApiError(
              requestError,
              "Unable to save employee advance."
            ),
        });

      } finally {

        setLoading(
          false
        );

      }

    };


  const selectedEmployee =
    employees.find(
      (
        employee
      ) =>
        String(
          employee?._id
        ) ===
        String(
          form.employeeId
        )
    );


  return ReactDOM
    .createPortal(

      <div className="employee-advance-modal-root">

        <button
          type="button"
          className="employee-advance-modal-backdrop"
          onClick={
            loading
              ?
              undefined
              :
              onClose
          }
          aria-label="Close"
        />


        <form
          className="employee-advance-modal"
          onSubmit={
            handleSubmit
          }
        >

          <header>

            <div>

              <span>
                <FiCreditCard />
              </span>


              <div>

                <small>
                  EMPLOYEE ADVANCE
                </small>

                <h3>
                  {
                    isEdit
                      ?
                      "Edit Advance"
                      :
                      "Add Advance"
                  }
                </h3>

              </div>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                loading
              }
            >
              <FiX />
            </button>

          </header>


          <div className="employee-advance-modal-body">

            {
              errors.form
                ?
                (
                  <div className="employee-advance-modal-error">
                    {errors.form}
                  </div>
                )
                :
                null
            }


            <div className="employee-advance-form-grid">

              <div className="employee-advance-form-full">

                <EmployeeSelector
                  employees={
                    employees
                  }
                  value={
                    form.employeeId
                  }
                  onChange={
                    (
                      employeeId
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,
                          employeeId,
                        })
                      )
                  }
                  label="Employee"
                  required
                  disabled={
                    isEdit ||
                    loading
                  }
                  error={
                    errors.employeeId
                  }
                />

              </div>


              <div className="employee-advance-form-field">

                <label>
                  Advance Date *
                </label>

                <input
                  type="date"
                  name="advanceDate"
                  value={
                    form.advanceDate
                  }
                  onChange={
                    handleChange
                  }
                  disabled={
                    loading
                  }
                />

                {
                  errors.advanceDate
                    ?
                    (
                      <small>
                        {errors.advanceDate}
                      </small>
                    )
                    :
                    null
                }

              </div>


              <div className="employee-advance-form-field">

                <label>
                  Advance Amount *
                </label>

                <input
                  type="text"
                  inputMode="decimal"
                  name="advanceAmount"
                  value={
                    form.advanceAmount
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="0"
                  disabled={
                    loading
                  }
                />

                {
                  errors.advanceAmount
                    ?
                    (
                      <small>
                        {errors.advanceAmount}
                      </small>
                    )
                    :
                    null
                }

              </div>


              <div className="employee-advance-form-field">

                <label>
                  Recovery Type *
                </label>

                <select
                  name="recoveryType"
                  value={
                    form.recoveryType
                  }
                  onChange={
                    handleChange
                  }
                  disabled={
                    loading
                  }
                >

                  {
                    ADVANCE_RECOVERY_TYPES.map(
                      (
                        type
                      ) => (

                        <option
                          key={
                            type
                          }
                          value={
                            type
                          }
                        >
                          {
                            formatEmployeeLabel(
                              type
                            )
                          }
                        </option>

                      )
                    )
                  }

                </select>

              </div>


              {
                [
                  "SALARY",
                  "BOTH",
                ].includes(
                  form.recoveryType
                )
                  ?
                  (
                    <div className="employee-advance-form-field">

                      <label>
                        Monthly Recovery Amount *
                      </label>

                      <input
                        type="text"
                        inputMode="decimal"
                        name="recoveryAmountPerMonth"
                        value={
                          form.recoveryAmountPerMonth
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="0"
                      />

                      {
                        errors.recoveryAmountPerMonth
                          ?
                          (
                            <small>
                              {
                                errors.recoveryAmountPerMonth
                              }
                            </small>
                          )
                          :
                          null
                      }

                    </div>
                  )
                  :
                  null
              }


              <div className="employee-advance-form-field employee-advance-form-full">

                <label>
                  Reason / Purpose *
                </label>

                <textarea
                  name="reason"
                  value={
                    form.reason
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Why is this advance being given?"
                />

                {
                  errors.reason
                    ?
                    (
                      <small>
                        {errors.reason}
                      </small>
                    )
                    :
                    null
                }

              </div>


              <div className="employee-advance-form-field employee-advance-form-full">

                <label>
                  Remark
                </label>

                <textarea
                  name="remark"
                  value={
                    form.remark
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Optional internal note"
                />

              </div>

            </div>


            {
              selectedEmployee
                ?
                (
                  <div className="employee-advance-employee-summary">

                    <div>

                      <span>
                        Employee
                      </span>

                      <strong>
                        {
                          selectedEmployee.name
                        }
                      </strong>

                    </div>


                    <div>

                      <span>
                        Current Net Salary
                      </span>

                      <strong>
                        {
                          formatEmployeeCurrency(
                            selectedEmployee
                              ?.salary
                              ?.netSalary
                          )
                        }
                      </strong>

                    </div>


                    <div>

                      <span>
                        New Advance
                      </span>

                      <strong>
                        {
                          formatEmployeeCurrency(
                            form.advanceAmount
                          )
                        }
                      </strong>

                    </div>

                  </div>
                )
                :
                null
            }

          </div>


          <footer>

            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                loading
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              disabled={
                loading
              }
            >

              <FiSave />

              {
                loading
                  ?
                  "Saving..."
                  :
                  isEdit
                    ?
                    "Update Advance"
                    :
                    "Create Advance"
              }

            </button>

          </footer>

        </form>

      </div>,

      document.body

    );

};


export default AddEditAdvanceModal;